import React, { useState, useEffect, useMemo, useRef } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes, faCheckDouble } from '@fortawesome/free-solid-svg-icons';
import { UI_COLORS, colorHelpers } from '../../utils/colors';
import { useTranslation } from 'react-i18next';
import purchaseOrderService from '../../services/purchaseOrderService';

const sanitizeNumber = (v) => {
    if (v === null || v === undefined || v === '') return '';
    const s = String(v);
    if (!/^\d*\.?\d*$/.test(s)) return s.slice(0, -1);
    return s;
};

const CompleteUnloadingForm = ({ isOpen, onClose, onSubmit, adviceId, productSizes = [], mode = 'complete' }) => {
    const { t } = useTranslation();
    const isEditMode = mode === 'edit';

    const [loading, setLoading] = useState(false);
    const [advice, setAdvice] = useState(null);

    const [formData, setFormData] = useState({
        empty_truck_weight: '',
        loaded_truck_weight: '',
        remark: '',
        internal_remark: '',
        items_update: [],
    });

    const [errors, setErrors] = useState({});
    const [submitting, setSubmitting] = useState(false);
    const [saving, setSaving] = useState(false);
    const lastFetchedIdRef = useRef(null);

    useEffect(() => {
        if (isOpen && adviceId && lastFetchedIdRef.current !== adviceId) {
            lastFetchedIdRef.current = adviceId;
            const fetchData = async () => {
                setLoading(true);
                try {
                    const adviceResult = await purchaseOrderService.getPurchaseAdviceById(adviceId);

                    if (adviceResult.success && adviceResult.data) {
                        const adviceData = adviceResult.data;
                        setAdvice(adviceData);
                        setFormData({
                            empty_truck_weight: isEditMode && adviceData.empty_truck_weight ? String(adviceData.empty_truck_weight) : '',
                            loaded_truck_weight: adviceData.loaded_truck_weight || adviceData.final_truck_weight || adviceData.empty_truck_weight || '',
                            remark: adviceData.remark || '',
                            internal_remark: adviceData.internal_remark || '',
                            items_update: (adviceData.items || []).map(item => ({
                                id: item.id,
                                product_size_id: item.product_size_id,
                                product_name: item.size_name || item.product_size?.alias_name || item.product_size?.product_name || 'Unknown Item',
                                ordered_quantity: parseFloat(item.quantity) || 0,
                                quantity: isEditMode && item.actual_quantity != null ? String(item.actual_quantity) : item.quantity,
                                unit: item.unit,
                                price: item.price,
                                length: item.length,
                                po_item_quantity: item.po_item_quantity,
                                actual_pieces: isEditMode && item.actual_pieces != null ? String(item.actual_pieces) : '',
                                remark: item.remark,
                            })),
                        });
                        setErrors({});
                    }
                } catch (error) {
                    console.error('Failed to fetch data', error);
                    lastFetchedIdRef.current = null;
                } finally {
                    setLoading(false);
                }
            };
            fetchData();
        }

        if (!isOpen) {
            lastFetchedIdRef.current = null;
        }
    }, [isOpen, adviceId, isEditMode]);

    // Pro-rata weight distribution logic
    useEffect(() => {
        if (!advice) return;

        const loadedWeight = parseFloat(formData.loaded_truck_weight) || 0;
        const emptyWeight = parseFloat(formData.empty_truck_weight) || 0;
        const netWeight = Math.round(loadedWeight - emptyWeight);

        if (emptyWeight > 0 && netWeight > 0) {
            setFormData(prev => {
                const itemsCount = prev.items_update.length;
                if (itemsCount === 0) return prev;

                // 1. Calculate estimated weight basis for each item to handle mixed units (m, kg, pcs)
                const itemBases = prev.items_update.map(item => {
                    const adviceItem = advice.items.find(ai => ai.id === item.id);
                    let basis = 0;
                    if (adviceItem) {
                        const sw = parseFloat(adviceItem.standard_weight) || 0;
                        const sl = parseFloat(adviceItem.standard_length) || 1;
                        const u = (item.unit || '').toLowerCase();
                        if (u === 'm') basis = item.ordered_quantity * (sw / sl);
                        else if (u === 'kg') basis = item.ordered_quantity;
                        else if (u === 'pcs' || u === 'pieces') basis = item.ordered_quantity * sw;
                        else basis = item.ordered_quantity;
                    } else {
                        basis = item.ordered_quantity;
                    }
                    return { id: item.id, basis };
                });

                const totalBasis = itemBases.reduce((acc, it) => acc + it.basis, 0);
                if (totalBasis <= 0) return prev;

                let distributedWeightTotal = 0;
                const newItemsUpdate = prev.items_update.map((item, index) => {
                    const adviceItem = advice.items.find(ai => ai.id === item.id);
                    const itemBasis = itemBases.find(ib => ib.id === item.id)?.basis || 0;
                    
                    // 2. Calculate Pro-rata Weight (Integer)
                    let itemWeight = 0;
                    if (index === itemsCount - 1) {
                        itemWeight = netWeight - distributedWeightTotal;
                    } else {
                        itemWeight = Math.round((itemBasis / totalBasis) * netWeight);
                        distributedWeightTotal += itemWeight;
                    }

                    // 3. Convert Weight back to Item's Input Unit (e.g. Meters)
                    let finalQuantityStr = String(itemWeight);
                    if (adviceItem) {
                        const sw = parseFloat(adviceItem.standard_weight) || 0;
                        const sl = parseFloat(adviceItem.standard_length) || 1;
                        const u = (item.unit || '').toLowerCase();
                        
                        if (u === 'm' && sw > 0) {
                            // meters = weight / (weight_per_meter)
                            const weightPerMeter = sw / sl;
                            finalQuantityStr = (itemWeight / weightPerMeter).toFixed(2);
                        }
                    }

                    return {
                        ...item,
                        quantity: finalQuantityStr,
                        actual_weight: itemWeight // Keep track of the raw distributed weight
                    };
                });

                // Check for actual changes to avoid unnecessary re-renders
                const hasChanged = newItemsUpdate.some((item, idx) => 
                    item.quantity !== prev.items_update[idx].quantity
                );

                return hasChanged ? { ...prev, items_update: newItemsUpdate } : prev;
            });
        } else if (!formData.empty_truck_weight || parseFloat(formData.empty_truck_weight) <= 0) {
            // FALLBACK: Reset to initial values from advice
            setFormData(prev => {
                const resetItems = prev.items_update.map(item => {
                    const adviceItem = advice.items.find(ai => ai.id === item.id);
                    if (!adviceItem) return item;
                    
                    // Reconstruct initial values based on current mode
                    const initialQty = isEditMode && adviceItem.actual_quantity != null ? String(adviceItem.actual_quantity) : adviceItem.quantity;
                    const initialPieces = isEditMode && adviceItem.actual_pieces != null ? String(adviceItem.actual_pieces) : '';
                    
                    return {
                        ...item,
                        quantity: initialQty,
                        actual_pieces: initialPieces,
                        actual_weight: undefined // Clear the pro-rata weight cache
                    };
                });

                const hasChanged = resetItems.some((it, idx) => 
                    it.quantity !== prev.items_update[idx].quantity || 
                    it.actual_pieces !== prev.items_update[idx].actual_pieces
                );

                return hasChanged ? { ...prev, items_update: resetItems } : prev;
            });
        }
    }, [formData.empty_truck_weight, formData.loaded_truck_weight, advice, isEditMode]);

    const calculatedData = useMemo(() => {
        if (!advice || !advice.items || formData.items_update.length === 0) return { items: [], isValid: false };

        const grossW = parseFloat(formData.loaded_truck_weight) || 0;
        const emptyW = parseFloat(formData.empty_truck_weight) || 0;
        const netW = emptyW > 0 ? Math.round(grossW - emptyW) : 0;

        let allQuantitiesEntered = true;
        let totalItemsWeight = 0;
        
        const finalItems = formData.items_update.map((item) => {
            if (item.quantity === '' || item.quantity === undefined) {
                allQuantitiesEntered = false;
            }
            
            // Use stored actual_weight if available, otherwise calculate from quantity
            let itemWeight = parseFloat(item.actual_weight);
            if (isNaN(itemWeight) || !item.actual_weight || emptyW === 0) {
                const adviceItem = advice.items.find(ai => ai.id === item.id);
                if (adviceItem) {
                    const sw = parseFloat(adviceItem.standard_weight) || 0;
                    const sl = parseFloat(adviceItem.standard_length) || 1;
                    const u = (item.unit || '').toLowerCase();
                    const qty = parseFloat(item.quantity) || 0;
                    if (u === 'm') itemWeight = qty * (sw / sl);
                    else itemWeight = qty;
                } else {
                    itemWeight = parseFloat(item.quantity) || 0;
                }
            }
            
            totalItemsWeight += itemWeight;
            return { ...item, derivedWeight: itemWeight };
        });

        return {
            items: finalItems,
            netW,
            totalItemsWeight,
            isValid: allQuantitiesEntered && emptyW > 0 && grossW > emptyW
        };
    }, [advice, formData.items_update, formData.empty_truck_weight, formData.loaded_truck_weight]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        if (name === 'empty_truck_weight' || name === 'loaded_truck_weight') {
            const sanitized = sanitizeNumber(value);
            setFormData(prev => ({ ...prev, [name]: sanitized }));
        } else {
            setFormData(prev => ({ ...prev, [name]: value }));
        }
        if (errors[name]) setErrors(prev => ({ ...prev, [name]: null }));
    };

    const handleItemChange = (index, field, value) => {
        const newItems = [...formData.items_update];
        if (field === 'quantity') {
            newItems[index][field] = sanitizeNumber(value);
            // Clear actual_weight so calculatedData recomputes it from the new quantity
            delete newItems[index].actual_weight;
        } else {
            newItems[index][field] = value;
        }
        setFormData(prev => ({ ...prev, items_update: newItems }));
    };

    const validate = () => {
        const newErrors = {};
        if (!formData.empty_truck_weight) newErrors.empty_truck_weight = t('purchaseOrders.forms.completeUnloadingForm.errors.invalidWeight');
        else if (isNaN(formData.empty_truck_weight) || parseFloat(formData.empty_truck_weight) <= 0) {
            newErrors.empty_truck_weight = t('purchaseOrders.forms.completeUnloadingForm.errors.invalidWeight');
        }

        if (!formData.loaded_truck_weight) newErrors.loaded_truck_weight = t('purchaseOrders.forms.completeUnloadingForm.errors.invalidWeight');
        else if (isNaN(formData.loaded_truck_weight) || parseFloat(formData.loaded_truck_weight) <= 0) {
            newErrors.loaded_truck_weight = t('purchaseOrders.forms.completeUnloadingForm.errors.invalidWeight');
        }

        if (!formData.remark || !formData.remark.trim()) newErrors.remark = t('common.formFields.errors.orderRemarkRequired');

        formData.items_update.forEach((item, index) => {
            if (item.quantity === '' || parseFloat(item.quantity) < 0) {
                newErrors[`item_${index}_quantity`] = t('purchaseOrders.forms.completeUnloadingForm.errors.invalidQuantity');
            }
        });

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const buildPayload = () => ({
        empty_truck_weight: formData.empty_truck_weight !== '' ? parseFloat(formData.empty_truck_weight) : null,
        loaded_truck_weight: formData.loaded_truck_weight !== '' ? parseFloat(formData.loaded_truck_weight) : null,
        remark: formData.remark || null,
        internal_remark: formData.internal_remark || '',
        items_update: formData.items_update.map(item => ({
            item_id: item.id,
            actual_quantity: parseFloat(item.quantity),
            actual_pieces: item.actual_pieces !== '' ? parseInt(item.actual_pieces) : null,
        })),
    });

    const handleSave = async () => {
        setSaving(true);
        try {
            await purchaseOrderService.saveUnloadingDraft(adviceId, buildPayload());
        } finally {
            setSaving(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!validate()) return;

        setSubmitting(true);
        try {
            await onSubmit(buildPayload());
        } finally {
            setSubmitting(false);
        }
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ backgroundColor: colorHelpers.withOpacity('#000000', 0.5) }}>
            <div className="w-full max-w-4xl bg-white rounded-2xl shadow-2xl max-h-[90vh] flex flex-col relative overflow-hidden" style={{ backgroundColor: UI_COLORS.BACKGROUNDS.CARD }}>
                {(submitting || loading || saving) && (
                    <div className="absolute inset-0 z-50 flex items-center justify-center" style={{ backgroundColor: colorHelpers.withOpacity(UI_COLORS.BACKGROUNDS.OVERLAY, 0.6) }}>
                        <div className="h-12 w-12 animate-spin rounded-full border-4 border-b-transparent" style={{ borderColor: UI_COLORS.BRAND.PRIMARY }}></div>
                    </div>
                )}

                <div className="flex items-center justify-between p-6 border-b flex-shrink-0" style={{ borderColor: UI_COLORS.BORDERS.LIGHT }}>
                    <h2 className="text-xl font-bold" style={{ color: UI_COLORS.TEXT.PRIMARY }}>{isEditMode ? t('common.formFields.labels.editUnloadingTitle') : t('purchaseOrders.forms.completeUnloadingForm.title')}</h2>
                    <button onClick={onClose} className="p-2 rounded-lg transition-colors hover:bg-gray-100" style={{ color: UI_COLORS.TEXT.MUTED }}>
                        <FontAwesomeIcon icon={faTimes} />
                    </button>
                </div>

                <div className="p-6 overflow-y-auto custom-scrollbar">
                    <p className="text-sm text-gray-500 mb-4">{t('purchaseOrders.forms.completeUnloadingForm.subtitle')}</p>

                    <div className="mb-6 space-y-4">
                        <div>
                            <label className="block text-[10px] font-bold uppercase tracking-wider mb-1" style={{ color: UI_COLORS.TEXT.MUTED }}>{t('common.formFields.labels.orderRemark')} <span>*</span></label>
                            <input
                                type="text"
                                name="remark"
                                value={formData.remark}
                                onChange={handleChange}
                                placeholder={t('common.formFields.placeholders.orderRemark')}
                                className="w-full px-4 py-2 rounded-xl border-2 transition-all outline-none text-sm"
                                style={{ borderColor: errors.remark ? UI_COLORS.STATUS.ERROR : UI_COLORS.FORMS.INPUT.border }}
                            />
                            {errors.remark && <p className="text-xs mt-1" style={{ color: UI_COLORS.STATUS.ERROR }}>{errors.remark}</p>}
                        </div>
                        <div>
                            <label className="block text-[10px] font-bold uppercase tracking-wider mb-1" style={{ color: UI_COLORS.TEXT.MUTED }}>{t('common.formFields.labels.internalRemarks')}</label>
                            <textarea
                                name="internal_remark"
                                value={formData.internal_remark}
                                onChange={handleChange}
                                placeholder={t('common.formFields.placeholders.internalRemarksOptional')}
                                className="w-full px-4 py-2 rounded-xl border-2 transition-all outline-none resize-none text-sm"
                                rows="2"
                                style={{ backgroundColor: '#f0fdf4', borderColor: '#86efac' }}
                            />
                        </div>
                    </div>

                    <form onSubmit={handleSubmit} className="space-y-6">
                        {advice && (
                            <div className="p-4 rounded-xl mb-4 space-y-2" style={{ backgroundColor: colorHelpers.withOpacity(UI_COLORS.BRAND.PRIMARY, 0.05), border: `1px solid ${colorHelpers.withOpacity(UI_COLORS.BRAND.PRIMARY, 0.1)}` }}>
                                <div className="flex justify-between items-center text-sm">
                                    <span className="font-semibold text-gray-500 uppercase tracking-wider text-[10px]">{t('purchaseOrders.forms.completeUnloadingForm.fields.poNumber')}</span>
                                    <span className="font-bold" style={{ color: UI_COLORS.TEXT.BLACK }}>{advice.po_number || t('common.symbols.emDash')}</span>
                                </div>
                                <div className="flex justify-between items-center text-sm">
                                    <span className="font-semibold text-gray-500 uppercase tracking-wider text-[10px]">{t('purchaseOrders.forms.completeUnloadingForm.fields.adviceNumber')}</span>
                                    <span className="font-bold" style={{ color: UI_COLORS.TEXT.PRIMARY }}>{advice.advice_number || t('common.symbols.emDash')}</span>
                                </div>
                            </div>
                        )}

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div>
                                <label className="block text-sm font-semibold mb-1" style={{ color: UI_COLORS.FORMS.LABEL }}>{t('purchaseOrders.forms.completeUnloadingForm.fields.loadedWeight.label')} <span>*</span></label>
                                <input
                                    type="number"
                                    name="loaded_truck_weight"
                                    value={formData.loaded_truck_weight}
                                    onChange={handleChange}
                                    onWheel={(e) => e.target.blur()}
                                    placeholder={t('common.formFields.placeholders.loadedWeight')}
                                    className="w-full px-4 py-3 rounded-xl border-2 transition-all outline-none [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
                                    style={{ borderColor: errors.loaded_truck_weight ? UI_COLORS.STATUS.ERROR : UI_COLORS.FORMS.INPUT.border }}
                                />
                                {errors.loaded_truck_weight && <p className="text-xs mt-1" style={{ color: UI_COLORS.STATUS.ERROR }}>{errors.loaded_truck_weight}</p>}
                            </div>

                            <div>
                                <label className="block text-sm font-semibold mb-1" style={{ color: UI_COLORS.FORMS.LABEL }}>{t('purchaseOrders.forms.completeUnloadingForm.fields.emptyWeight.label')} <span>*</span></label>
                                <input
                                    type="number"
                                    name="empty_truck_weight"
                                    value={formData.empty_truck_weight}
                                    onChange={handleChange}
                                    onWheel={(e) => e.target.blur()}
                                    placeholder={t('purchaseOrders.forms.completeUnloadingForm.placeholders.truckWeight')}
                                    className="w-full px-4 py-3 rounded-xl border-2 transition-all outline-none [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
                                    style={{ borderColor: errors.empty_truck_weight ? UI_COLORS.STATUS.ERROR : UI_COLORS.FORMS.INPUT.border }}
                                />
                                {errors.empty_truck_weight && <p className="text-xs mt-1" style={{ color: UI_COLORS.STATUS.ERROR }}>{errors.empty_truck_weight}</p>}
                            </div>
                        </div>

                        <div>
                            <h3 className="text-lg font-semibold mb-3" style={{ color: UI_COLORS.TEXT.PRIMARY }}>{t('purchaseOrders.forms.completeUnloadingForm.headings.items')}</h3>
                            <div className="space-y-3">
                                {calculatedData.items.map((item, index) => (
                                    <div key={item.id} className="p-4 rounded-xl border flex flex-col md:flex-row md:items-center gap-4 transition-colors hover:border-gray-300" style={{ borderColor: UI_COLORS.BORDERS.LIGHT, backgroundColor: UI_COLORS.BACKGROUNDS.MAIN }}>
                                        <div className="flex-1">
                                            <div className="flex items-center gap-2">
                                                <p className="font-medium" style={{ color: UI_COLORS.TEXT.PRIMARY }}>{item.product_name}</p>
                                                {item.load_by_pieces && (
                                                    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-bold bg-teal-50 text-teal-700 border border-teal-200">{t('common.formFields.labels.loadByPieces')}</span>
                                                )}
                                            </div>
                                            <div className="flex items-center gap-3 mt-1">
                                                <p className="text-xs" style={{ color: UI_COLORS.TEXT.SECONDARY }}>
                                                    {t('purchaseOrders.forms.completeUnloadingForm.fields.ordered')}: {item.ordered_quantity} {item.unit}
                                                </p>
                                                {(() => {
                                                    const pcs = advice?.items?.find(ai => ai.id === item.id)?.quantity_in_pieces ?? item.quantity_in_pieces;
                                                    return pcs != null ? (
                                                        <div className="flex items-center gap-1">
                                                            <span className="text-sm font-bold text-blue-600">{pcs}</span>
                                                            <span className="text-[10px] font-bold uppercase text-gray-400 tracking-tighter">{t('common.units.pcs')}</span>
                                                        </div>
                                                    ) : null;
                                                })()}
                                            </div>
                                            {item.remark && item.remark !== 'none' && (
                                                <div className="mt-2 inline-block px-2 py-0.5 rounded text-[10px] font-bold bg-yellow-100 text-yellow-800 border border-yellow-200 uppercase tracking-tight">
                                                    {item.remark}
                                                </div>
                                            )}
                                        </div>
                                        <div className="w-full md:w-36">
                                            <label className="block text-xs font-semibold mb-1" style={{ color: UI_COLORS.FORMS.LABEL }}>{t('purchaseOrders.forms.completeUnloadingForm.fields.unloadedQty')}</label>
                                            <div className="relative flex items-center">
                                                <input
                                                    type="number"
                                                    value={item.quantity}
                                                    onChange={(e) => handleItemChange(index, 'quantity', e.target.value)}
                                                    onWheel={(e) => e.target.blur()}
                                                    className="w-full px-3 py-2 rounded-lg border-2 transition-all outline-none focus:border-blue-500 pr-10 [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
                                                    style={{ borderColor: errors[`item_${index}_quantity`] ? UI_COLORS.STATUS.ERROR : UI_COLORS.FORMS.INPUT.border }}
                                                />
                                                <span className="absolute right-3 text-xs font-medium" style={{ color: UI_COLORS.TEXT.MUTED }}>{item.unit}</span>
                                            </div>
                                            {errors[`item_${index}_quantity`] && <p className="text-xs mt-1" style={{ color: UI_COLORS.STATUS.ERROR }}>{errors[`item_${index}_quantity`]}</p>}
                                        </div>
                                        <div className="w-full md:w-32">
                                            <label className="block text-xs font-semibold mb-1" style={{ color: UI_COLORS.FORMS.LABEL }}>{t('common.formFields.labels.actualPieces')}</label>
                                            <input
                                                type="number"
                                                value={item.actual_pieces}
                                                onChange={(e) => handleItemChange(index, 'actual_pieces', e.target.value)}
                                                onWheel={(e) => e.target.blur()}
                                                placeholder="0"
                                                className="w-full px-3 py-2 rounded-lg border-2 transition-all outline-none focus:border-blue-500 [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
                                                style={{ borderColor: UI_COLORS.FORMS.INPUT.border }}
                                            />
                                        </div>
                                        <div className="md:w-32 flex flex-col justify-center bg-white p-2 rounded border text-sm shadow-sm" title={t('common.messages.estimatedWeightTooltip')}>
                                            <div className="flex flex-col">
                                                <span className="text-gray-400 text-[10px] uppercase font-bold tracking-tighter">{t('common.formFields.labels.calcWt')}</span>
                                                <span className="font-bold text-gray-900">{parseFloat(item.derivedWeight || 0).toFixed(0)} {t('common.units.kg')}</span>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>

                        <div className="flex justify-between items-center gap-4 pt-4 border-t" style={{ borderColor: UI_COLORS.BORDERS.LIGHT }}>
                            <div className="flex flex-col gap-1">
                                <div className="flex items-center gap-2 px-4 py-2 rounded-lg" style={{ backgroundColor: colorHelpers.withOpacity(UI_COLORS.BRAND.PRIMARY, 0.1) }}>
                                    <span className="text-[10px] font-bold uppercase text-gray-500 tracking-wider">{t('common.formFields.labels.netTruckWeight')}</span>
                                    <span className="text-lg font-black" style={{ color: UI_COLORS.TEXT.PRIMARY }}>{calculatedData.netW || '0'} {t('common.units.kg')}</span>
                                </div>
                                {(isEditMode || calculatedData.emptyW > 0) && Math.abs(calculatedData.netW - Math.round(calculatedData.totalItemsWeight)) > 0 && (
                                    <div className="flex items-center gap-1 ml-1">
                                        <span className="h-1.5 w-1.5 rounded-full bg-red-500 animate-pulse"></span>
                                        <span className="text-[10px] text-red-600 font-black uppercase tracking-tighter">
                                            {calculatedData.totalItemsWeight > calculatedData.netW ? 'Weight Overage' : 'Weight Shortage'}: {Math.abs(calculatedData.netW - Math.round(calculatedData.totalItemsWeight))} {t('common.units.kg')}
                                        </span>
                                    </div>
                                )}
                            </div>
                            <div className="flex gap-4">
                                <button type="button" onClick={onClose} className="px-6 py-3 rounded-xl font-medium transition-colors" style={{ backgroundColor: '#F3F4F6', color: '#374151' }}>{t('common.buttons.cancel')}</button>
                                <button type="button" onClick={handleSave} disabled={saving || submitting} className="px-6 py-3 rounded-xl font-medium transition-colors disabled:opacity-50" style={{ backgroundColor: '#E5E7EB', color: '#374151' }}>{saving ? t('common.buttons.saving') : t('common.buttons.save')}</button>
                                <button type="submit" disabled={!calculatedData.isValid || submitting} className="px-6 py-3 rounded-xl font-semibold text-white shadow-lg transition-all hover:shadow-xl active:scale-95 disabled:opacity-50 disabled:scale-100" style={{ backgroundColor: UI_COLORS.BRAND.PRIMARY, color: UI_COLORS.TEXT.PRIMARY }}>
                                    <FontAwesomeIcon icon={faCheckDouble} className="mr-2" />
                                    {t('purchaseOrders.forms.completeUnloadingForm.buttons.complete')}
                                </button>
                            </div>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
};

export default CompleteUnloadingForm;
